import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Download, Lock, Unlock, ChevronRight, Menu, X, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Framework data
const frameworkClusters = [
  {
    id: 'strategy',
    title: 'Strategy & Readiness',
    description: 'Foundational assessment and prioritisation frameworks that structure AI adoption from day one.',
    image: 'https://images.pexels.com/photos/14528981/pexels-photo-14528981.jpeg?auto=compress&cs=tinysrgb&w=800',
    frameworks: [
      { id: 'ai-readiness-snapshot', name: 'AI Readiness Snapshot', access: 'public' },
      { id: 'full-ai-readiness-assessment', name: 'Full AI Readiness Assessment', access: 'registered' },
      { id: 'use-case-prioritisation-matrix', name: 'Use Case Prioritisation Matrix', access: 'registered' },
    ]
  },
  {
    id: 'workflow',
    title: 'Workflow Intelligence',
    description: 'Operational workflow redesign and AI integration frameworks.',
    image: 'https://images.unsplash.com/photo-1640358336205-942f48bd940f?w=800&q=80',
    frameworks: [
      { id: 'ai-project-canvas', name: 'AI Project Canvas', access: 'public' },
      { id: 'workflow-redesign-framework', name: 'Workflow Redesign Framework', access: 'registered' },
      { id: 'hitl-aitl-design-framework', name: 'HITL / AITL Design Framework', access: 'registered' },
    ]
  },
  {
    id: 'governance',
    title: 'Governance & Risk',
    description: 'Lifecycle governance, risk classification, and monitoring infrastructure.',
    image: 'https://images.unsplash.com/photo-1659982182150-b76bb83fdcd5?w=800&q=80',
    frameworks: [
      { id: 'ai-risk-register', name: 'AI Risk Register', access: 'registered' },
      { id: 'ai-governance-framework', name: 'AI Governance Framework', access: 'registered' },
      { id: 'model-monitoring-drift-framework', name: 'Model Monitoring & Drift Framework', access: 'registered' },
    ]
  },
  {
    id: 'decision',
    title: 'Decision Infrastructure',
    description: 'Decision intelligence, evaluation systems, and value tracking mechanisms.',
    image: 'https://images.pexels.com/photos/28887851/pexels-photo-28887851.jpeg?auto=compress&cs=tinysrgb&w=800',
    frameworks: [
      { id: 'value-realisation-tracker', name: 'Value Realisation Tracker', access: 'public' },
      { id: 'decision-audit-framework', name: 'Decision Audit Framework', access: 'registered' },
      { id: 'ai-evaluation-framework', name: 'AI Evaluation Framework', access: 'registered' },
    ]
  }
];

const failurePoints = [
  'Undefined ownership and accountability',
  'No governance lifecycle',
  'Workflow redesign ignored',
  'No monitoring or evaluation structure',
  'No audit or decision traceability'
];

const AiOperatingSystem = () => {
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [selectedFramework, setSelectedFramework] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [recognized, setRecognized] = useState(null);
  const [checkingEmail, setCheckingEmail] = useState(false);

  // Function to trigger actual file download
  const triggerDownload = async (clusterId, frameworkId, frameworkName) => {
    try {
      // Check if file exists
      const response = await axios.get(`${API}/api/framework-file/${clusterId}/${frameworkId}`);
      if (response.data.exists && response.data.path) {
        // Open file in new tab or trigger download
        window.open(response.data.path, '_blank');
        toast.success(`Downloading ${frameworkName}`);
      } else {
        toast.info('Framework document coming soon', {
          description: 'This document is being prepared and will be available shortly.'
        });
      }
    } catch (error) {
      toast.info('Framework document coming soon', {
        description: 'This document is being prepared and will be available shortly.'
      });
    }
  };

  const handlePublicDownload = async (framework, clusterId) => {
    // Track the download
    try {
      await axios.post(`${API}/api/framework-downloads`, {
        framework_name: framework.name,
        cluster: clusterId,
        access_type: 'public'
      });
    } catch (error) {
      console.log('Download tracking failed:', error);
    }
    
    // Trigger actual download
    await triggerDownload(clusterId, framework.id, framework.name);
  };

  const handleRegisteredDownload = (framework, clusterId) => {
    setSelectedFramework({ ...framework, clusterId });
    setEmail('');
    setRecognized(null);
    setEmailModalOpen(true);
  };

  const checkEmail = async (emailValue) => {
    if (!emailValue || !emailValue.includes('@')) {
      setRecognized(null);
      return;
    }
    
    setCheckingEmail(true);
    try {
      const response = await axios.post(`${API}/api/framework-check-email`, { email: emailValue });
      if (response.data.exists) {
        setRecognized(response.data);
      } else {
        setRecognized(null);
      }
    } catch (error) {
      console.log('Email check failed:', error);
      setRecognized(null);
    } finally {
      setCheckingEmail(false);
    }
  };

  const handleEmailChange = (e) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    
    // Debounce email check
    if (newEmail.includes('@') && newEmail.includes('.')) {
      const timeoutId = setTimeout(() => checkEmail(newEmail), 500);
      return () => clearTimeout(timeoutId);
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }

    setSubmitting(true);

    try {
      await axios.post(`${API}/api/framework-register-email`, {
        email: email,
        framework_name: selectedFramework.name,
        cluster: selectedFramework.clusterId
      });

      toast.success(recognized ? 'Welcome back!' : 'Access granted!', {
        description: `You now have access to ${selectedFramework.name}.`
      });

      const frameworkId = selectedFramework.id;
      const clusterId = selectedFramework.clusterId;
      const frameworkName = selectedFramework.name;

      setEmailModalOpen(false);
      setEmail('');
      setRecognized(null);

      // Trigger actual download
      await triggerDownload(clusterId, frameworkId, frameworkName);

    } catch (error) {
      toast.error('Something went wrong', {
        description: 'Please try again or contact us directly.'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const scrollToFrameworks = () => {
    setMobileMenuOpen(false);
    document.getElementById('frameworks')?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToContact = () => {
    setMobileMenuOpen(false);
    window.location.href = '/#contact';
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200" data-testid="ai-os-navigation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="flex items-center justify-between h-14 sm:h-16">
            <Link to="/" className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors" data-testid="back-to-home">
              <ArrowLeft size={16} className="sm:w-[18px] sm:h-[18px]" />
              <span className="text-xs sm:text-sm font-medium">Back to CAACI</span>
            </Link>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <button onClick={scrollToFrameworks} className="text-gray-700 hover:text-blue-600 transition-colors text-sm font-medium" data-testid="nav-frameworks">
                Frameworks
              </button>
              <button onClick={scrollToContact} className="text-gray-700 hover:text-blue-600 transition-colors text-sm font-medium" data-testid="nav-contact">
                Contact
              </button>
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-gray-700"
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-gray-200 bg-white" data-testid="mobile-menu">
            <div className="px-4 py-3 space-y-2">
              <button onClick={scrollToFrameworks} className="block w-full text-left px-3 py-2 text-gray-700 hover:bg-gray-100 rounded text-sm">
                Frameworks
              </button>
              <button onClick={scrollToContact} className="block w-full text-left px-3 py-2 text-gray-700 hover:bg-gray-100 rounded text-sm">
                Contact
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-20 sm:pt-24 md:pt-32 pb-12 sm:pb-16 md:pb-24 px-4 sm:px-6 md:px-12" data-testid="ai-os-hero">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center">
            <div className="lg:col-span-7">
              <p className="text-[10px] sm:text-xs font-bold uppercase tracking-widest text-gray-500 mb-3 sm:mb-4">
                Implementation Infrastructure
              </p>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-tight text-gray-900 mb-4 sm:mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                The CAACI AI Operating System
              </h1>
              <p className="text-lg sm:text-xl md:text-2xl text-gray-600 mb-3 sm:mb-4 font-medium">
                Governance. Workflow Intelligence. Decision Infrastructure.
              </p>
              <p className="text-sm sm:text-base md:text-lg text-gray-600 leading-relaxed mb-6 sm:mb-8">
                The CAACI AI Operating System provides structured implementation frameworks that move organisations from AI experimentation to governed production systems. It defines the operating backbone required for enterprise-scale AI.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <Button 
                  onClick={scrollToFrameworks}
                  className="bg-black text-white hover:bg-gray-900 rounded-none h-11 sm:h-12 px-6 sm:px-8 font-medium text-sm sm:text-base"
                  data-testid="cta-access-frameworks"
                >
                  Access Frameworks
                  <ChevronRight className="ml-2" size={16} />
                </Button>
                <Button 
                  variant="outline"
                  className="border-gray-900 text-gray-900 hover:bg-gray-50 rounded-none h-11 sm:h-12 px-6 sm:px-8 font-medium text-sm sm:text-base"
                  onClick={scrollToContact}
                  data-testid="cta-discuss-implementation"
                >
                  Discuss Implementation
                </Button>
              </div>
            </div>
            <div className="lg:col-span-5 hidden lg:block">
              <div className="aspect-square bg-gray-100 border border-gray-200">
                <img 
                  src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&q=80" 
                  alt="Enterprise AI Infrastructure"
                  className="w-full h-full object-cover grayscale opacity-90"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why It Matters Section */}
      <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 md:px-12 bg-gray-50 border-y border-gray-200" data-testid="why-it-matters">
        <div className="max-w-4xl mx-auto">
          <p className="text-[10px] sm:text-xs font-bold uppercase tracking-widest text-gray-500 mb-3 sm:mb-4 text-center">
            The Implementation Gap
          </p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight text-gray-900 mb-4 sm:mb-6 md:mb-8 text-center" style={{ fontFamily: 'Playfair Display, serif' }}>
            From AI Pilots to Operational Systems
          </h2>
          <p className="text-sm sm:text-base md:text-lg text-gray-600 leading-relaxed mb-6 sm:mb-8 md:mb-10 text-center">
            Most organisations fail at AI not because of technology limitations, but because of systemic gaps in implementation infrastructure. Common failure points include:
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-6 sm:mb-8 md:mb-10">
            {failurePoints.map((point, index) => (
              <div key={index} className="flex items-start space-x-3 p-3 sm:p-4 bg-white border border-gray-200">
                <span className="text-red-500 font-bold text-xs sm:text-sm flex-shrink-0">0{index + 1}</span>
                <span className="text-gray-700 text-xs sm:text-sm">{point}</span>
              </div>
            ))}
          </div>
          <p className="text-sm sm:text-base md:text-lg text-gray-900 font-medium text-center">
            The CAACI AI Operating System addresses these gaps through structured implementation infrastructure designed for enterprise environments.
          </p>
        </div>
      </section>

      {/* Framework Clusters Section */}
      <section id="frameworks" className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 md:px-12" data-testid="framework-clusters">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <p className="text-[10px] sm:text-xs font-bold uppercase tracking-widest text-gray-500 mb-3 sm:mb-4">
              Capability Architecture
            </p>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight text-gray-900 mb-3 sm:mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Implementation Frameworks
            </h2>
            <p className="text-sm sm:text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
              Structured operational assets organised across four capability domains.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 md:gap-12">
            {frameworkClusters.map((cluster) => (
              <Card key={cluster.id} className="border border-gray-200 rounded-none shadow-none hover:border-gray-400 transition-colors" data-testid={`cluster-${cluster.id}`}>
                <div className="aspect-[16/9] overflow-hidden border-b border-gray-200">
                  <img 
                    src={cluster.image} 
                    alt={cluster.title}
                    className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-500"
                  />
                </div>
                <CardContent className="p-4 sm:p-6 md:p-8">
                  <h3 className="text-lg sm:text-xl md:text-2xl font-medium text-gray-900 mb-2 sm:mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {cluster.title}
                  </h3>
                  <p className="text-gray-600 text-xs sm:text-sm mb-4 sm:mb-6 leading-relaxed">
                    {cluster.description}
                  </p>
                  <div className="space-y-2 sm:space-y-3">
                    {cluster.frameworks.map((framework, fIndex) => (
                      <div 
                        key={fIndex} 
                        className="flex items-center justify-between p-3 sm:p-4 bg-gray-50 border border-gray-200 hover:bg-gray-100 transition-colors gap-2"
                        data-testid={`framework-${cluster.id}-${fIndex}`}
                      >
                        <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
                          {framework.access === 'public' ? (
                            <Unlock size={14} className="text-green-600 flex-shrink-0 sm:w-4 sm:h-4" />
                          ) : (
                            <Lock size={14} className="text-gray-400 flex-shrink-0 sm:w-4 sm:h-4" />
                          )}
                          <span className="text-xs sm:text-sm font-medium text-gray-900 truncate">{framework.name}</span>
                        </div>
                        <div className="flex items-center space-x-2 sm:space-x-3 flex-shrink-0">
                          <span className={`text-[10px] sm:text-xs font-bold uppercase tracking-wider hidden sm:inline ${framework.access === 'public' ? 'text-green-600' : 'text-gray-500'}`}>
                            {framework.access === 'public' ? 'Open' : 'Email'}
                          </span>
                          <Button
                            size="sm"
                            variant={framework.access === 'public' ? 'default' : 'outline'}
                            className={`rounded-none h-7 sm:h-8 px-2 sm:px-3 text-[10px] sm:text-xs ${framework.access === 'public' ? 'bg-black text-white hover:bg-gray-900' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                            onClick={() => framework.access === 'public' 
                              ? handlePublicDownload(framework, cluster.id) 
                              : handleRegisteredDownload(framework, cluster.id)
                            }
                            data-testid={`download-${cluster.id}-${fIndex}`}
                          >
                            <Download size={12} className="mr-1 sm:w-[14px] sm:h-[14px]" />
                            <span className="hidden sm:inline">Access</span>
                            <span className="sm:hidden">Get</span>
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 md:px-12 bg-gray-900" data-testid="cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight text-white mb-4 sm:mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
            Ready to Operationalise AI?
          </h2>
          <p className="text-sm sm:text-base md:text-lg text-gray-400 mb-6 sm:mb-8 max-w-2xl mx-auto">
            Discuss how the CAACI AI Operating System can be tailored to your organisation's implementation requirements.
          </p>
          <Button 
            className="bg-white text-gray-900 hover:bg-gray-100 rounded-none h-11 sm:h-12 px-6 sm:px-8 font-medium text-sm sm:text-base"
            onClick={scrollToContact}
            data-testid="cta-final-contact"
          >
            Schedule Advisory Discussion
            <ChevronRight className="ml-2" size={16} />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 sm:py-12 px-4 sm:px-6 md:px-12 border-t border-gray-200" data-testid="ai-os-footer">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-xs sm:text-sm text-gray-500 text-center sm:text-left">
            © 2026 Centre for Applied AI. All rights reserved.
          </p>
          <Link to="/" className="text-xs sm:text-sm text-gray-600 hover:text-gray-900 transition-colors">
            Return to CAACI Home
          </Link>
        </div>
      </footer>

      {/* Email Modal - Simple & Clean */}
      <Dialog open={emailModalOpen} onOpenChange={setEmailModalOpen}>
        <DialogContent className="sm:max-w-md rounded-none border border-gray-200 mx-4" data-testid="email-modal" aria-describedby="email-modal-description">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl font-semibold text-gray-900" style={{ fontFamily: 'Playfair Display, serif' }}>
              Access Framework
            </DialogTitle>
            {selectedFramework && (
              <p id="email-modal-description" className="text-xs sm:text-sm text-gray-600 mt-2">
                Enter your email to access: <span className="font-medium text-gray-900">{selectedFramework.name}</span>
              </p>
            )}
          </DialogHeader>
          
          <form onSubmit={handleEmailSubmit} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs sm:text-sm font-medium text-gray-700">Work Email</Label>
              <div className="relative">
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={handleEmailChange}
                  className="rounded-none h-11 sm:h-12 bg-gray-50 border-gray-200 focus:border-gray-900 focus:ring-0 text-sm pr-10"
                  placeholder="your.email@company.com"
                  data-testid="input-email"
                />
                {checkingEmail && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                  </div>
                )}
                {recognized && !checkingEmail && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <Check size={18} className="text-green-600" />
                  </div>
                )}
              </div>
              {recognized && (
                <p className="text-xs text-green-600 flex items-center gap-1">
                  <Check size={12} />
                  Welcome back! You're already registered.
                </p>
              )}
            </div>

            <Button
              type="submit"
              className="w-full rounded-none h-11 sm:h-12 bg-black text-white hover:bg-gray-900 text-sm font-medium"
              disabled={submitting || !email.includes('@')}
              data-testid="btn-submit-email"
            >
              {submitting ? 'Processing...' : recognized ? 'Download Framework' : 'Get Access'}
            </Button>

            <p className="text-[10px] sm:text-xs text-gray-500 text-center">
              We respect your privacy. No spam, just framework access.
            </p>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AiOperatingSystem;
